import { useState, useRef } from "react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/modern/components/ui/dropdown-menu";
import { CopyToBoardDialog } from "../../CopyToBoardDialog";
import { useEntityManagerContext } from "../context/EntityManagerContext";
import { EllipsisVertical, X } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { capitalizeText, showUnlinkRelatedEntityPrompt } from "@/other/utils/utils";
import { useConfirm } from "@omit/react-confirm-dialog";
import { toast } from "sonner";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { getIsArchivedForBoard } from "@/other/utils/otherUtils";

interface EntityNameButtonProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function EntityNameButton({ open: controlledOpen, onOpenChange: controlledOnOpenChange }: EntityNameButtonProps) {
  const { record, entityConfig: config, entityType, closeEntityManager, copyEntityLink } = useEntityManagerContext();
  const { project } = useSwivuBryntumContext();
  const [internalOpen, setInternalOpen] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const confirm = useConfirm();

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (controlledOnOpenChange ?? (() => {})) : setInternalOpen;

  const handleRecordCopyTo = () => {
    setOpen(false);
    setShowCopyDialog(true);
  }

  const handleRecordArchiveToggle = async () => {
    const selectedBoard = getSavedBoardSelection();
    const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);

    if (!record.archived && record.swivuUnlinkPromptConfig) {
      const isArchived = await showUnlinkRelatedEntityPrompt(record.swivuUnlinkPromptConfig);
      if (isArchived) {
        setOpen(false)
        if (!canShowArchivedRecords) closeEntityManager();
      };
    } else {
      record.archived = !record.archived;
      setOpen(false);
      if (!canShowArchivedRecords) closeEntityManager();
    }
  }

  const handleRecordDelete = async () => {
    const entityTypeName = capitalizeText(entityType);
    const confirmed = await confirm({
      title: `Delete ${entityTypeName}`,
      description: `Are you sure you want to delete "${record?.name || 'this ' + entityTypeName}"? This action cannot be undone.`,
      confirmButton: {
        variant: 'destructive',
        title: 'Delete ' + entityTypeName
      },
      cancelButton: {
        variant: 'ghost'
      }
    });
    if (!confirmed) return;
    try {
      const store = project.getCrudStore(config.storeKey as string);
      if (!store) throw new Error("[EntityNameButton] Cannot find store")
      store.remove(record.id);
      toast.success(`${entityTypeName} "${record?.name}" deleted successfully`);
      setOpen(false);
      closeEntityManager();
    } catch (error) {
      toast.error('Something went wrong! Unable to delete ' + entityTypeName);
      console.error('Failed to delete ' + entityTypeName + ':', error);
    }
  }

  const handleRecordShare = async () => {
    await confirm({
      title: 'Share',
      description: 'How would you like to share?',
      customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
        <>
          <Button variant="ghost" size="icon-sm" className="absolute top-4 right-4" onClick={cancel}>
            <X className="w-4 h-4" />
           <span className="sr-only">Cancel</span>
          </Button>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={async () => {
                const url = await copyEntityLink(undefined, undefined, undefined, true);
                const textToCopy = `${record.name}\n${url}`;
                try {
                  await navigator.clipboard.writeText(textToCopy);
                  toast.success("Description with link copied to clipboard");
                } catch (error) {
                  console.error('Failed to copy:', error);
                  toast.error('Failed to copy to clipboard');
                }
                confirm();
              }}
            >
              Description with URL
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                await copyEntityLink(undefined, undefined, "Copied to clipboard");
                confirm();
              }}
            >
              URL Only
            </Button>
          </div>
        </>
      )
    });
    setOpen(false);
  }

  const ContextMenuComponent = config.contextMenu;

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="entityHeader" size="icon" className="focus-visible:ring-0 !h-8">
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent ref={contentRef} align="start" className="w-56">
          {ContextMenuComponent && (
            <ContextMenuComponent
              record={record}
              onClose={() => {
                setOpen(false);
              }}
              onItemClick={(item: { name: string }) => {
                switch (item.name) {
                  case "copyTo":
                    handleRecordCopyTo();
                    break;
                  case "share":
                    handleRecordShare();
                    break;
                  case "archive":
                    handleRecordArchiveToggle();
                    break;
                  case "delete":
                    handleRecordDelete();
                    break;
                  default:
                    console.warn(`[EntityNameButton] Unknown item name: ${item.name} for ${entityType}`);
                    break;
                }
              }}
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Copy to Board Dialog */}
      <CopyToBoardDialog open={showCopyDialog} onOpenChange={setShowCopyDialog} record={record} entityType={entityType} entityName={record?.name} />
    </>
  );
}
